/* eslint-disable camelcase */

import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { useLocalization } from "@fluent/react";
import { formatNumber, Alert } from "@narmi/design_system";
import { FormHeaderWithIcon, ProgressButtons } from "cerulean";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import Account from "byzantine/src/Account";
import { useSubmitLoanPayment } from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import type { LoanAutopayData } from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import { useCurrentUser } from "../../../contexts/CurrentUserContext";
import AccountContext from "../../../contexts/AccountContext";
import { getDisplayedFrequency } from "../../FrequencySelector";
import LoanReviewRow from "../LoanReviewRow";
import type { LoanPaymentFormType } from "./types";
import type { LoanReceiptData } from "./LoanPaymentReceipt";

interface LoanPaymentReviewProps {
  form: LoanPaymentFormType;
  goBack: () => void;
  onSuccess: (data: LoanReceiptData) => void;
  autopayData: LoanAutopayData | null;
}

const LoanPaymentReview = ({
  form,
  goBack,
  onSuccess,
  autopayData,
}: LoanPaymentReviewProps) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const { accounts } = useContext(AccountContext) as { accounts: Account[] };
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4() as UUID);
  const [cards, setCards] = useState<any[]>([]);

  const { submitLoanPayment, loading: isSubmitting } =
    useSubmitLoanPayment(idempotencyKey);

  const isCardPayment = form.values.fundingMethod === "card";

  // Helper function to get localized strings with card/bank variants
  const getLocalizedString = useCallback(
    (cardKey: string, bankKey: string, options?: Record<string, any>) => {
      const key = isCardPayment ? cardKey : bankKey;
      return l10n.getString(key, options);
    },
    [isCardPayment, l10n]
  );

  // Scroll to top when review screen loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadCards = async () => {
      if (currentUser && form.values.fundingMethod === "card") {
        try {
          const userCards = await currentUser.getFundingCards();
          setCards(userCards || []);
        } catch (error) {
          // Error loading cards
        }
      }
    };

    loadCards();
  }, [currentUser, form.values.fundingMethod]);

  const selectedLoan = useMemo(
    () => accounts.find((a) => a.id === form.values.toAccountId),
    [accounts, form.values.toAccountId]
  );

  const selectedFromAccount = useMemo(
    () => accounts.find((a) => a.id === form.values.fromAccountId),
    [accounts, form.values.fromAccountId]
  );

  const getPaymentContent = useCallback(() => {
    if (isCardPayment) {
      return cards.find((card: any) => card.id === form.values.cardUUID)
        ?.description;
    }
    return selectedFromAccount?.getShortDescription();
  }, [isCardPayment, cards, form.values.cardUUID, selectedFromAccount]);

  const getDateContent = useCallback(() => {
    if (isCardPayment) {
      return DateTime.now().toFormat("MM/dd/yyyy");
    }
    return DateTime.fromFormat(form.values.date, "M/d/yyyy").toFormat(
      "MM/dd/yyyy"
    );
  }, [isCardPayment, form.values.date]);

  const getAmountAndIsMinPayment = useCallback(() => {
    if (form.values.paymentType === "amountDue") {
      const minimumPayment = selectedLoan?.loan_details?.minimum_payment;
      return {
        amount: minimumPayment ? formatNumber(minimumPayment) : "0",
        isMinPayment: true,
      };
    }
    return {
      amount:
        typeof form.values.amount === "number"
          ? formatNumber(form.values.amount / 100) // Convert from cents to dollars
          : form.values.amount,
      isMinPayment: false,
    };
  }, [form.values.paymentType, form.values.amount, selectedLoan]);

  const getSuccessReceiptData = useCallback((): LoanReceiptData => {
    const { amount, isMinPayment } = getAmountAndIsMinPayment();

    return {
      loanDescription: selectedLoan?.getShortDescription() ?? "",
      fromDescription: getPaymentContent(),
      amount: isMinPayment
        ? l10n.getString("option-minimum-payment", { minimumPayment: amount })
        : amount,
      date: getDateContent(),
      frequency:
        !isCardPayment && form.values.frequency !== "ONCE"
          ? getDisplayedFrequency(form.values.frequency, l10n) ?? undefined
          : undefined,
      memo: !isCardPayment ? form.values.memo : undefined,
      isScheduled:
        form.values.frequency !== "ONCE" ||
        DateTime.fromFormat(form.values.date, "M/d/yyyy").toFormat(
          "yyyy-MM-dd"
        ) !== DateTime.now().toFormat("yyyy-MM-dd"),
    };
  }, [
    form.values.frequency,
    form.values.date,
    form.values.memo,
    getAmountAndIsMinPayment,
    getDateContent,
    getPaymentContent,
    isCardPayment,
    l10n,
    selectedLoan,
  ]);

  const handleSubmit = useCallback(() => {
    const formattedDate = DateTime.fromFormat(
      form.values.date,
      "M/d/yyyy"
    ).toFormat("yyyy-MM-dd");

    submitLoanPayment(
      {
        ...form.values,
        date: formattedDate,
      },
      {
        onSuccess: () => {
          onSuccess(getSuccessReceiptData());
        },
        onError: () => {
          // Error handling is already done in the hook via toast
          // Fallback error handling
          goBack();
        },
        onFinally: () => {
          // Generate new idempotency key for next submission
          setIdempotencyKey(uuidv4() as UUID);
        },
      }
    );
  }, [
    form.values,
    submitLoanPayment,
    onSuccess,
    goBack,
    getSuccessReceiptData,
  ]);

  const { amount, isMinPayment } = getAmountAndIsMinPayment();

  const dateLabel =
    form.values.frequency !== "ONCE"
      ? l10n.getString("label-review-date-recurring")
      : l10n.getString("label-review-date-once");

  const displayedAmount = isMinPayment
    ? l10n.getString("option-minimum-payment", { minimumPayment: amount })
    : amount;

  // Format frequency for display using utility function
  const displayedFrequency = getDisplayedFrequency(form.values.frequency, l10n);

  const { paymentDate = "", paymentAmount = "" } = autopayData || {};

  return (
    <div className="loan-payment-page">
      <FormHeaderWithIcon
        headerText={getLocalizedString(
          "loan-paybycard-review-page-heading",
          "loan-paydown-review-page-heading"
        )}
      />
      <div className="loan-payment-form-card">
        <h4 className="nds-sans">
          {getLocalizedString(
            "loan-paybycard-payment-title",
            "loan-paydown-payment-title",
            {
              loan: selectedLoan?.getShortDescription() || "",
            }
          )}
        </h4>
        <div className="margin--top--xl">
          <LoanReviewRow
            label={l10n.getString("label-from")}
            content={getPaymentContent()}
          />
          <LoanReviewRow
            label={l10n.getString("label-amount")}
            content={displayedAmount}
          />
          {!isCardPayment && displayedFrequency && (
            <LoanReviewRow
              label={l10n.getString("label-frequency")}
              content={displayedFrequency}
            />
          )}
          <LoanReviewRow label={dateLabel} content={getDateContent()} />
          {!isCardPayment && form.values.memo && (
            <LoanReviewRow
              label={l10n.getString("label-memo")}
              content={form.values.memo}
            />
          )}
        </div>
      </div>

      {paymentDate && (
        <div style={{ paddingTop: "var(--space-xl)" }}>
          <Alert isActive isDismissable={false} kind="warn">
            {l10n.getString("loan-autopay-payment-review-warning", {
              paymentDate,
              hasPaymentAmount: `${!!paymentAmount}`,
              paymentAmount: paymentAmount || "",
            })}
          </Alert>
        </div>
      )}

      <div className="form-buttons-with-spacing">
        <ProgressButtons
          onBack={goBack}
          backLabel={l10n.getString("button-back")}
          onNext={handleSubmit}
          nextLabel={getLocalizedString(
            "loan-paybycard-make-payment-button",
            "loan-paydown-make-payment-button"
          )}
          isNextLoading={isSubmitting}
          nextTestId="submit-loan-payment"
        />
      </div>
    </div>
  );
};

export default LoanPaymentReview;
